import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AtSign, CornerUpLeft, Bell, CheckCheck, ChevronLeft, ExternalLink, Trash2 } from 'lucide-react';
import { ActivityItem, ActivityTab } from '../types';
import { activityService, activityTabOf } from '../services/activityService';
import { formatRelativeTime } from './ChannelListItem';
import { openExternalUrl } from '../utils/openExternal';
import { useRovingTabs } from '../hooks/useRovingTabs';
import { useLongPress } from '../hooks/useLongPress';
import { linkOrigin, sourceIconOf } from '../utils/systemSource';
import { IS_COARSE_POINTER } from '../utils/pointer';
import { rememberNoticeRef } from '../services/calendarService';
import { useToast } from '../contexts/ToastContext';
import { ContextMenu } from './ContextMenu';
import { ConfirmDialog } from './ConfirmDialog';

/**
 * 통합 알림함 패널 (Phase 2) — 멘션/답글/리액션 + 외부 시스템 알림(전자결재 등) 모아보기
 *
 * 데이터는 자체 fetch + socketService가 재발행하는 CustomEvent(activity:new / activity:read)
 * 구독으로 독립 동작한다 (App.tsx 콜백 미사용 — D6). 뱃지 카운트는 App이 같은 이벤트를
 * 별도 구독하므로 이 컴포넌트는 목록과 탭별 숫자만 책임진다.
 *
 * 탭: 서버(GET /activity/unread-count의 tabs)가 정한다 — 시스템 출처가 하나라도 있는 사용자만
 * '전체 / 대화 / 전자결재 …' 탭 줄이 보이고, 없으면 예전 알림함과 똑같다. 목록은 탭별로 캐시해
 * 탭을 오갈 때 다시 부르지 않는다(패널을 닫았다 열면 새로 받는다).
 *
 * 데스크톱: 가운데 목록 패널로 마운트 / 모바일: 전체화면 오버레이(onClose 전달 시 헤더에 뒤로가기)
 *
 * 삭제(2026-11-05): 행 하나는 PC 휴지통 버튼(마우스를 올리면)·우클릭 / 휴대폰 길게 누르기 → '삭제'(안 읽은 것도),
 * 머리의 휴지통은 지금 탭의 **읽은** 알림만 한 번에 지운다(확인창). 다른 세션에는 서버가
 * activity:read { removedId } / { removedRead, tab }으로 알린다.
 */

const TYPE_LABEL: Record<string, string> = {
  mention: '님이 나를 멘션했습니다',
  reply: '님이 답장했습니다',
  reaction: '님이 반응했습니다',
};

const EMPTY_TEXT: Record<string, [string, string]> = {
  all: ['받은 알림이 없습니다', '멘션·답장·반응과 시스템 알림이 여기에 모입니다'],
  people: ['받은 알림이 없습니다', '멘션·답장·반응이 여기에 모입니다'],
};

const TAB_STORAGE_KEY = 'activityTab';

// 마지막으로 소비한 탭 요청 번호(모듈 스코프 — 패널 언마운트를 넘어 유지된다)
let consumedRequestSeq = 0;

function readStoredTab(): string {
  try {
    return localStorage.getItem(TAB_STORAGE_KEY) || 'all';
  } catch {
    return 'all';
  }
}

function storeTab(tab: string) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // 저장 실패는 무시 — 기기별 편의 기능
  }
}

interface TabList {
  items: ActivityItem[];
  hasMore: boolean;
}

function belongsTo(tab: string, item: Pick<ActivityItem, 'source'>): boolean {
  return tab === 'all' || activityTabOf(item) === tab;
}

interface ActivityPanelProps {
  /** 알림 클릭 → 해당 채널·메시지로 이동 (App의 handleOpenActivityTarget 배선) */
  onOpenTarget: (channelId: number, messageId: number) => void;
  /** 모바일 오버레이에서만 전달 — 헤더에 뒤로가기 표시 */
  onClose?: () => void;
  /** 알림(푸시·토스트) 클릭으로 열릴 때 보여 줄 탭. seq가 바뀔 때마다 그 탭으로 전환한다 */
  requestedTab?: { tab: string; seq: number } | null;
  /** 시스템 알림 클릭 → 우측(모바일은 전체화면) 출처별 봇 대화를 그 알림 위치로 연다.
   *  미전달이면 예전처럼 원문 문서를 바로 연다 */
  onOpenSystemFeed?: (source: string, focusId: number) => void;
}

export const ActivityPanel: React.FC<ActivityPanelProps> = ({ onOpenTarget, onClose, requestedTab, onOpenSystemFeed }) => {
  const [tabs, setTabs] = useState<ActivityTab[]>([]);
  // 아직 소비하지 않은 알림 클릭 요청이 있으면 첫 렌더부터 그 탭으로(엉뚱한 탭을 한 번 불러오지 않게)
  const [activeTab, setActiveTab] = useState<string>(() => (
    requestedTab?.tab && requestedTab.seq > consumedRequestSeq ? requestedTab.tab : readStoredTab()
  ));
  const [lists, setLists] = useState<Record<string, TabList>>({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabsLoaded, setTabsLoaded] = useState(false);
  const [menu, setMenu] = useState<{ item: ActivityItem; x: number; y: number } | null>(null);
  const [confirmDeleteRead, setConfirmDeleteRead] = useState(false);
  const { showToast } = useToast();

  // 이벤트 리스너에서 최신 탭 목록을 읽기 위한 ref (리스너 재등록 방지)
  const tabsRef = useRef<ActivityTab[]>([]);
  tabsRef.current = tabs;

  const refreshSummary = useCallback(() => {
    return activityService.getUnreadSummary()
      .then(res => {
        setTabs(res.tabs);
        setTabsLoaded(true);
      })
      .catch(err => {
        console.error('Failed to fetch activity tabs:', err);
        setTabsLoaded(true); // 탭 없이 '전체'로라도 보여 준다
      });
  }, []);

  // 열릴 때마다 탭 목록 fresh fetch (서랍 패턴)
  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  // 모든 탭 캐시에서 조건에 맞는 항목을 뺀다(삭제·회수 공용). 다 빠졌는데 서버에 더 있으면(hasMore)
  // 그 탭 캐시를 버려 다시 받는다 — 빈 목록에는 '더 보기' 커서(마지막 항목 id)가 없다
  const removeWhere = useCallback((pred: (it: ActivityItem) => boolean) => {
    setLists(prev => {
      const next: Record<string, TabList> = {};
      for (const [k, v] of Object.entries(prev)) {
        const items = v.items.filter(it => !pred(it));
        if (items.length === 0 && v.hasMore) continue;
        next[k] = items.length === v.items.length ? v : { ...v, items };
      }
      return next;
    });
  }, []);

  // 알림 클릭으로 특정 탭 요청 — 한 번 소비한 요청은 패널을 닫았다 다시 열어도 재적용하지 않는다
  // (App이 마지막 요청을 계속 들고 있으므로, 없으면 사용자가 고른 탭이 매번 되돌아간다)
  useEffect(() => {
    if (!requestedTab?.tab || requestedTab.seq <= consumedRequestSeq) return;
    consumedRequestSeq = requestedTab.seq;
    setActiveTab(requestedTab.tab);
    storeTab(requestedTab.tab);
  }, [requestedTab?.seq, requestedTab?.tab]);

  // 탭 목록이 정해지면 활성 탭 검증 — 탭이 없거나(시스템 알림 없음) 사라진 탭이면 '전체'
  const effectiveTab = useMemo(() => {
    if (!tabsLoaded) return activeTab;
    if (tabs.length === 0) return 'all';
    return tabs.some(t => t.key === activeTab) ? activeTab : 'all';
  }, [tabsLoaded, tabs, activeTab]);

  // 활성 탭 목록 로드 (캐시에 없을 때만)
  const current = lists[effectiveTab];
  useEffect(() => {
    if (!tabsLoaded || lists[effectiveTab]) return;
    let cancelled = false;
    setError(null);
    activityService.getActivity(effectiveTab)
      .then(res => {
        if (cancelled) return;
        setLists(prev => ({ ...prev, [effectiveTab]: { items: res.items, hasMore: res.hasMore } }));
      })
      .catch(err => {
        console.error('Failed to fetch activity:', err);
        if (!cancelled) setError('알림을 불러오지 못했습니다.');
      });
    return () => { cancelled = true; };
    // lists 전체를 deps에 넣으면 prepend마다 재실행된다 — 캐시 유무만 본다
  }, [tabsLoaded, effectiveTab, !!current]);

  const selectTab = useCallback((tab: string) => {
    setActiveTab(tab);
    storeTab(tab);
  }, []);

  // 실시간 갱신 — socketService의 CustomEvent 재발행 구독
  useEffect(() => {
    const markReadWhere = (pred: (it: ActivityItem) => boolean) => {
      const now = new Date().toISOString();
      setLists(prev => {
        const next: Record<string, TabList> = {};
        for (const [k, v] of Object.entries(prev)) {
          next[k] = { ...v, items: v.items.map(it => (!it.readAt && pred(it) ? { ...it, readAt: now } : it)) };
        }
        return next;
      });
    };

    const onNew = (e: Event) => {
      const item = (e as CustomEvent).detail as ActivityItem;
      if (!item?.id) return;
      const own = activityTabOf(item);
      setLists(prev => {
        const next = { ...prev };
        for (const k of Object.keys(prev)) {
          if (!belongsTo(k, item)) continue;
          const list = prev[k];
          if (list.items.some(it => it.id === item.id)) continue;
          next[k] = { ...list, items: [item, ...list.items] };
        }
        return next;
      });
      const known = tabsRef.current;
      if (item.source && !known.some(t => t.key === own)) {
        // 처음 보는 출처(이 사용자의 첫 시스템 알림 또는 새로 붙은 시스템) — 탭 목록을 다시 받는다
        refreshSummary();
        return;
      }
      setTabs(prev => prev.map(t => (t.key === 'all' || t.key === own ? { ...t, unread: t.unread + 1 } : t)));
    };

    const onRead = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        id?: number; all?: boolean; tab?: string; removedId?: number; source?: string | null;
        ids?: number[]; count?: number; removedRead?: boolean;
      };
      if (Array.isArray(detail?.ids)) {
        // 대화방을 읽어 그 방 알림(전부 source=null, '대화' 탭)이 함께 읽음 처리됨.
        // 데스크톱은 이 패널을 띄운 채 오른쪽 채팅창에서 방을 읽으므로 여기서 맞춰야 목록이 낡지 않는다
        const idSet = new Set(detail.ids);
        const n = typeof detail.count === 'number' ? detail.count : detail.ids.length;
        const own = activityTabOf({ source: null });
        markReadWhere(it => idSet.has(it.id));
        setTabs(prev => prev.map(t => (
          t.key === 'all' || t.key === own ? { ...t, unread: Math.max(0, t.unread - n) } : t
        )));
      } else if (detail?.all) {
        const tab = detail.tab || 'all';
        markReadWhere(it => belongsTo(tab, it));
        refreshSummary();
      } else if (detail?.removedRead) {
        // 어느 기기에서든 '읽은 알림 삭제' — 그 탭의 읽은 항목만 뺀다. 출처 행이 다 사라지면
        // 그 탭이 없어지므로 탭 목록을 다시 받는다(사라진 탭을 보고 있었으면 effectiveTab이 '전체'로 돌린다)
        const tab = detail.tab || 'all';
        removeWhere(it => !!it.readAt && belongsTo(tab, it));
        refreshSummary();
      } else if (detail?.removedId) {
        // 리액션 취소 회수·알림 삭제 — 목록에서 제거 (미읽음 여부는 모르므로 숫자는 서버 재조회)
        const removedId = Number(detail.removedId);
        removeWhere(it => it.id === removedId);
        refreshSummary();
      } else if (detail?.id) {
        markReadWhere(it => it.id === detail.id);
        // 서버 에코는 실제로 안읽음→읽음이 된 경우에만 온다 → 여기서만 숫자를 줄인다
        const own = activityTabOf({ source: detail.source ?? null });
        setTabs(prev => prev.map(t => (
          t.key === 'all' || t.key === own ? { ...t, unread: Math.max(0, t.unread - 1) } : t
        )));
      }
    };
    window.addEventListener('activity:new', onNew);
    window.addEventListener('activity:read', onRead);
    return () => {
      window.removeEventListener('activity:new', onNew);
      window.removeEventListener('activity:read', onRead);
    };
  }, [refreshSummary, removeWhere]);

  const handleLoadMore = useCallback(async () => {
    const list = lists[effectiveTab];
    if (loadingMore || !list || list.items.length === 0) return;
    setLoadingMore(true);
    const tab = effectiveTab;
    try {
      const res = await activityService.getActivity(tab, list.items[list.items.length - 1].id);
      setLists(prev => {
        const cur = prev[tab];
        if (!cur) return prev;
        const seen = new Set(cur.items.map(it => it.id));
        return { ...prev, [tab]: { items: [...cur.items, ...res.items.filter(it => !seen.has(it.id))], hasMore: res.hasMore } };
      });
    } catch (err) {
      console.error('Failed to load more activity:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [lists, effectiveTab, loadingMore]);

  const handleItemClick = (item: ActivityItem, openDocument = false) => {
    if (!item.readAt) {
      // fire-and-forget — 숫자는 서버의 activity:read 에코가 줄인다 (멀티세션 일관)
      activityService.markRead(item.id).catch(() => {});
      const now = new Date().toISOString();
      setLists(prev => {
        const next: Record<string, TabList> = {};
        for (const [k, v] of Object.entries(prev)) {
          next[k] = { ...v, items: v.items.map(it => (it.id === item.id ? { ...it, readAt: now } : it)) };
        }
        return next;
      });
    }
    if (item.type === 'system') {
      // 시스템 알림은 채널이 없다 — 출처별 봇 대화(가상 뷰)를 그 알림 위치로 연다.
      // 행의 '문서 열기' 아이콘은 예전처럼 원문 시스템(인트라넷 결재 문서 등)으로 바로 간다
      // 일정 알림은 봇 대화가 아니라 캘린더의 그 날짜로 간다(App이 source로 분기) — 이동 날짜는 행의 ref에 있다
      if (item.source === 'calendar' && item.ref) rememberNoticeRef(item.id, item.ref);
      if (!openDocument && item.source && onOpenSystemFeed) {
        onOpenSystemFeed(item.source, item.id);
      } else if (item.link) {
        void openExternalUrl(item.link);
      }
      return;
    }
    if (item.channelId != null && item.messageId != null) {
      onOpenTarget(item.channelId, item.messageId);
    }
  };

  const items = current?.items || [];
  const hasMore = current?.hasMore || false;
  const loading = !tabsLoaded || !current;

  const handleReadAll = () => {
    activityService.markAllRead(effectiveTab).catch(() => {});
    const now = new Date().toISOString();
    setLists(prev => {
      const next: Record<string, TabList> = {};
      for (const [k, v] of Object.entries(prev)) {
        next[k] = { ...v, items: v.items.map(it => (!it.readAt && belongsTo(effectiveTab, it) ? { ...it, readAt: now } : it)) };
      }
      return next;
    });
  };

  // 삭제 실패 — 낙관적으로 뺀 항목을 되돌리려고 캐시를 버리고 지금 탭을 다시 받는다
  const recoverFromDeleteError = useCallback((err: unknown) => {
    console.error('Failed to delete activity:', err);
    showToast({ type: 'error', message: '알림을 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    setLists({});
    refreshSummary();
  }, [showToast, refreshSummary]);

  // 단건 삭제 — 확인창 없이 바로(한 건이라 잃는 것이 작다). 배지·탭 숫자는 서버의 removedId 에코가 맞춘다
  const handleDelete = useCallback((item: ActivityItem) => {
    setMenu(null);
    removeWhere(it => it.id === item.id);
    activityService.deleteActivity(item.id).catch(recoverFromDeleteError);
  }, [removeWhere, recoverFromDeleteError]);

  const handleDeleteRead = () => {
    const tab = effectiveTab;
    setConfirmDeleteRead(false);
    removeWhere(it => !!it.readAt && belongsTo(tab, it));
    activityService.deleteRead(tab)
      .then(n => {
        showToast(n > 0
          ? { type: 'success', message: `읽은 알림 ${n}건을 삭제했습니다` }
          : { type: 'info', message: '삭제할 읽은 알림이 없습니다' });
      })
      .catch(recoverFromDeleteError);
  };

  const openMenu = useCallback((item: ActivityItem, pos: { x: number; y: number }) => {
    setMenu({ item, x: pos.x, y: pos.y });
  }, []);

  const hasUnread = tabs.length > 0
    ? (tabs.find(t => t.key === effectiveTab)?.unread || 0) > 0 || items.some(it => !it.readAt)
    : items.some(it => !it.readAt);
  // 화면에 읽은 항목이 없어도 아래 페이지에 있을 수 있다(hasMore) — 그때는 서버가 0건이면 안내한다
  const canDeleteRead = !loading && (items.some(it => !!it.readAt) || hasMore);
  const activeTabLabel = effectiveTab === 'all' ? null : tabs.find(t => t.key === effectiveTab)?.label;

  const tabKeys = useMemo(() => tabs.map(t => t.key), [tabs]);
  const roving = useRovingTabs(tabKeys, effectiveTab, selectTab);

  const emptyText = EMPTY_TEXT[effectiveTab] || [
    `${tabs.find(t => t.key === effectiveTab)?.label || '시스템'} 알림이 없습니다`,
    '새 알림이 오면 여기에 모입니다',
  ];

  return (
    <div className="flex flex-col h-full bg-white safe-area-top select-none">
      {/* 헤더 */}
      <div className="h-14 md:h-16 flex items-center justify-between px-4 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-1">
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 -ml-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title="닫기"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          <h1 className="text-lg font-bold text-slate-800">알림</h1>
        </div>
        <div className="flex items-center gap-0.5 -mr-1">
          {/* 패널 최소 폭(288px)에 글자 버튼 두 개가 들어가지 않아 아이콘으로 둔다 — 무엇을 지우는지는 확인창이 말한다 */}
          <button
            onClick={() => setConfirmDeleteRead(true)}
            disabled={!canDeleteRead}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-default"
            title={effectiveTab === 'all' ? '읽은 알림 삭제' : '이 탭의 읽은 알림 삭제'}
            aria-label="읽은 알림 삭제"
          >
            <Trash2 size={16} />
          </button>
          <button
            onClick={handleReadAll}
            disabled={!hasUnread}
            className="flex items-center gap-1 px-2 py-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-default"
            title={effectiveTab === 'all' ? '모두 읽음 처리' : '이 탭의 알림을 모두 읽음 처리'}
          >
            <CheckCheck size={16} />
            모두 읽음
          </button>
        </div>
      </div>

      {/* 탭 — 시스템 알림 출처가 있는 사용자에게만 보인다 */}
      {tabs.length > 0 && (
        <div
          {...roving.tablistProps}
          aria-label="알림 종류"
          className="flex gap-1 px-3 py-2 border-b border-slate-100 overflow-x-auto shrink-0"
        >
          {tabs.map(t => {
            const active = t.key === effectiveTab;
            return (
              <button
                key={t.key}
                {...roving.tabProps(t.key)}
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  active ? 'bg-brand-100 text-brand-700 font-semibold' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {t.label}
                {t.unread > 0 && (
                  <span
                    className={`min-w-[18px] h-[18px] px-1 rounded-full text-[11px] leading-[18px] text-center tabular-nums ${
                      active ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {t.unread > 99 ? '99+' : t.unread}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* 목록 */}
      <div className="flex-1 overflow-y-auto" {...(tabs.length > 0 ? roving.panelProps(effectiveTab) : {})}>
        {error && !current ? (
          <div className="flex items-center justify-center h-32 text-sm text-brand-600">{error}</div>
        ) : loading ? (
          <div className="flex items-center justify-center h-32 text-sm text-slate-400">불러오는 중...</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2 text-slate-300">
            <Bell size={32} />
            <p className="text-sm text-slate-400">{emptyText[0]}</p>
            <p className="text-xs text-slate-300">{emptyText[1]}</p>
          </div>
        ) : (
          <>
            {items.map(item => item.type === 'system' ? (
              <SystemRow key={item.id} item={item} onClick={handleItemClick} onMenu={openMenu} onDelete={handleDelete} />
            ) : (
              <ConversationRow key={item.id} item={item} onClick={handleItemClick} onMenu={openMenu} onDelete={handleDelete} />
            ))}
            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="w-full py-3 text-sm text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                {loadingMore ? '불러오는 중...' : '더 보기'}
              </button>
            )}
          </>
        )}
      </div>

      {/* 행 메뉴 — PC 우클릭 / 휴대폰 길게 누르기 (대화방 목록과 같은 light + backdrop) */}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          variant="light"
          backdrop
          onClose={() => setMenu(null)}
          items={[{ key: 'delete', label: '삭제', icon: Trash2, danger: true, onSelect: () => handleDelete(menu.item) }]}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDeleteRead}
        title="읽은 알림 삭제"
        message={`${activeTabLabel ? `'${activeTabLabel}' 탭의 ` : ''}읽은 알림을 모두 삭제할까요? 안 읽은 알림은 남고, 삭제한 알림은 되돌릴 수 없습니다.`}
        confirmLabel="삭제"
        danger
        onConfirm={handleDeleteRead}
        onCancel={() => setConfirmDeleteRead(false)}
      />
    </div>
  );
};

interface RowProps {
  item: ActivityItem;
  onClick: (item: ActivityItem, openDocument?: boolean) => void;
  /** 행 메뉴 열기 — PC 우클릭·휴대폰 길게 누르기 공용 */
  onMenu: (item: ActivityItem, pos: { x: number; y: number }) => void;
  onDelete: (item: ActivityItem) => void;
}

/** 행 본문 버튼에 붙이는 길게 누르기 + 우클릭. ChannelListItem과 같은 순서 — 롱프레스 훅의 onContextMenu
 *  (터치 제스처 중 네이티브 메뉴 억제)를 먼저 태우고, 막혔으면 터치 경로라 우클릭 메뉴를 열지 않는다 */
function useRowMenuHandlers(item: ActivityItem, onMenu: RowProps['onMenu']) {
  const { handlers } = useLongPress(pos => onMenu(item, pos));
  return {
    ...handlers,
    onContextMenu: (e: React.MouseEvent) => {
      handlers.onContextMenu(e);
      if (e.defaultPrevented) return;
      e.preventDefault();
      onMenu(item, { x: e.clientX, y: e.clientY });
    },
  };
}

// 삭제 버튼이 나타나는 동안 그 자리의 시각을 감춘다 — 터치 기기는 버튼이 없고, 누른 뒤 hover가 남는
// 브라우저가 있어 시각이 사라진 채로 보일 수 있으므로 걸지 않는다
const HIDE_ON_ROW_HOVER = IS_COARSE_POINTER ? '' : 'group-hover:invisible';

/** PC 전용 행 삭제 버튼 — 마우스를 올리거나 키보드로 오면 시각 자리에 겹쳐 나타난다.
 *  ⚠ 터치 기기에서는 렌더하지 않는다: 보이지 않는 버튼을 잘못 눌러 지우는 사고를 막는다(휴대폰은 길게 누르기).
 *  바탕은 행 hover 바탕(slate-50)과 같아야 밑의 시각 글자를 덮는다 — 그래서 hover 바탕은 행 wrapper가 소유한다 */
const RowDeleteButton: React.FC<{ onClick: () => void; className?: string }> = ({ onClick, className = 'right-0' }) => (
  <button
    onClick={onClick}
    className={`absolute inset-y-0 ${className} w-11 flex items-center justify-center bg-slate-50 text-slate-400 hover:text-brand-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity`}
    title="알림 삭제"
    aria-label="알림 삭제"
  >
    <Trash2 size={16} />
  </button>
);

/** 대화 알림 행(멘션·답장·반응) — 누르면 그 대화방의 그 메시지로 간다 */
const ConversationRow: React.FC<RowProps> = ({ item, onClick, onMenu, onDelete }) => {
  const menuHandlers = useRowMenuHandlers(item, onMenu);
  return (
    <div
      className={`group relative border-b border-slate-50 transition-colors hover:bg-slate-50 ${item.readAt ? '' : 'bg-brand-50'}`}
    >
      <button
        onClick={() => onClick(item)}
        {...menuHandlers}
        className="w-full flex items-start gap-3 px-4 py-2.5 text-left touch-callout-none"
      >
        {/* 타입 아이콘 */}
        <span
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
            item.type === 'mention'
              ? 'bg-brand-100 text-brand-600'
              : item.type === 'reply'
                ? 'bg-emerald-100 text-emerald-600'
                : 'bg-amber-100'
          }`}
        >
          {item.type === 'mention' ? (
            <AtSign size={16} />
          ) : item.type === 'reply' ? (
            <CornerUpLeft size={16} />
          ) : (
            <span className="text-base leading-none">{item.emoji || '👍'}</span>
          )}
        </span>

        <span className="flex flex-col min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="text-sm text-slate-800 truncate">
              <b>{item.actorName}</b>
              {TYPE_LABEL[item.type] || '님의 알림'}
            </span>
            <span className={`text-xs text-slate-400 shrink-0 ml-auto ${HIDE_ON_ROW_HOVER}`}>{formatRelativeTime(item.createdAt)}</span>
          </span>
          {/* 방 이름 · 미리보기를 한 줄로 — 행이 3줄이면 목록 한 화면에 몇 건 안 들어간다 */}
          <span className="text-xs truncate mt-0.5">
            <span className="text-slate-400">{item.channelName || `${item.actorName}님과의 대화`}</span>
            {item.preview && (
              <>
                <span className="text-slate-300"> · </span>
                <span className="text-slate-500">{item.preview}</span>
              </>
            )}
          </span>
        </span>

        {/* 미읽음 dot */}
        {!item.readAt && <span className="w-2 h-2 rounded-full bg-brand-600 shrink-0 mt-2" />}
      </button>
      {!IS_COARSE_POINTER && <RowDeleteButton onClick={() => onDelete(item)} />}
    </div>
  );
};

/** 외부 시스템 알림 행 — 출처 아이콘 · 알림 제목 / 보낸 곳 · 문서 제목 (2줄).
 *  행 클릭 = 봇 대화, 우측 아이콘 = 문서 바로 열기. 버튼 안에 버튼을 둘 수 없어 형제로 나란히 둔다.
 *  링크 원점은 줄을 따로 쓰지 않고 '문서 열기' 아이콘의 툴팁에 싣는다(원점까지만 노출하는 규칙 그대로) */
const SystemRow: React.FC<RowProps> = ({ item, onClick, onMenu, onDelete }) => {
  const Icon = sourceIconOf(item.source);
  const origin = linkOrigin(item.link);
  const menuHandlers = useRowMenuHandlers(item, onMenu);
  return (
    <div
      className={`group relative flex items-stretch border-b border-slate-50 transition-colors hover:bg-slate-50 ${item.readAt ? '' : 'bg-brand-50'}`}
    >
    <button
      onClick={() => onClick(item)}
      {...menuHandlers}
      className="flex-1 min-w-0 flex items-start gap-3 pl-4 pr-2 py-2.5 text-left touch-callout-none"
    >
      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-blue-100 text-blue-600">
        <Icon size={16} />
      </span>
      <span className="flex flex-col min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="text-sm text-slate-800 truncate">{item.title || `${item.actorName} 알림`}</span>
          <span className={`text-xs text-slate-400 shrink-0 ml-auto ${HIDE_ON_ROW_HOVER}`}>{formatRelativeTime(item.createdAt)}</span>
        </span>
        <span className="text-xs truncate mt-0.5">
          <span className="text-slate-400">{item.actorName}</span>
          {item.preview && (
            <>
              <span className="text-slate-300"> · </span>
              <span className="text-slate-500">{item.preview}</span>
            </>
          )}
        </span>
      </span>
      {!item.readAt && <span className="w-2 h-2 rounded-full bg-brand-600 shrink-0 mt-2" />}
    </button>
    {/* 링크가 없는 알림도 같은 폭을 비워 둔다 — 행마다 시각 열이 들쭉날쭉해지지 않게.
        삭제 버튼도 이 열 왼쪽(right-10)에 겹쳐 나타나므로 이 폭이 그 자리를 정한다 */}
    {origin ? (
      <button
        onClick={() => onClick(item, true)}
        className="shrink-0 w-10 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        title={`문서 열기 (${origin})`}
        aria-label="문서 열기"
      >
        <ExternalLink size={16} />
      </button>
    ) : (
      <span className="shrink-0 w-10" aria-hidden="true" />
    )}
    {!IS_COARSE_POINTER && <RowDeleteButton onClick={() => onDelete(item)} className="right-10" />}
    </div>
  );
};
